import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useArchiveClientMutation, useLazyGetArchivedClientsQuery, useLazyGetClientsQuery } from "store/api/clientApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import globalStyles from "assets/scss/index.module.scss"
import { ReactComponent as IconArchiveBox } from "assets/svg/archive-box.svg"

import { convertClientsToTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useCustomerEditModal from "hooks/useCustomerEditModal"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { DeleteModal } from "modals/DeleteModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"
import { WidgetCustomersMobileUsage } from "components/widgets/WidgetCustomersMobileUsage"
import { WidgetNewCustomersTrend } from "components/widgets/WidgetNewCustomersTrend"

import CustomersPageHeader from "./CustomersPageHeader"
import { getCustomersTableHeaderCells } from "./getCustomersTableHeaderCells"

const WidgetsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 0 16px 0;
  justify-content: flex-start;
`

export const Customers = () => {
  const navigate = useNavigate()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [showArchived, setShowArchived] = useState(urlQueryParams.showArchived === "true")
  const [triggerGetClients, { isFetching, data }] = useLazyGetClientsQuery()
  const [triggerGetArchivedClients, { isFetching: isFetchingArchived, data: archivedClients }] =
    useLazyGetArchivedClientsQuery()
  const [archiveClient] = useArchiveClientMutation()
  const [isFirstRender, setFirstRender] = useState(true)
  const [toBeArchivedCustomer, setToBeArchivedCustomer] = useState<number | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<number[]>([])
  const { onShowEditCustomerModal, renderEditCustomerModal } = useCustomerEditModal()
  const userProfile = useAppSelector(state => state.user.userProfile)

  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()

  useEffect(() => {
    setUrlQueryParams({
      showArchived: null,
    })
    triggerGetTableColumns({ list_name: "contractor_customers_list" })
  }, [])

  useEffect(() => {
    if (showArchived) {
      setUrlQueryParams({
        notes: null,
        app_activated: null,
      })
    }
  }, [showArchived, setUrlQueryParams])

  const tableHeaderCells = useMemo((): UITypes.TableHeaderCell[] => {
    const baseHeaderCells = getCustomersTableHeaderCells(showArchived)
    return columnsData?.data?.config
      ? reorderTableHeaderCells(baseHeaderCells, columnsData?.data?.config)
      : baseHeaderCells
  }, [columnsData, showArchived])

  const headerCells = useMemo(
    (): UITypes.TableHeaderCell[] => [
      ...(showArchived
        ? [
            {
              title: "",
              name: "checkbox",
              cellComponent: "checkbox",
              size: "mini",
            } as UITypes.TableHeaderCell,
          ]
        : []),
      ...tableHeaderCells,
    ],
    [showArchived, tableHeaderCells],
  )

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await (showArchived ? triggerGetArchivedClients(params) : triggerGetClients(params))
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleArchiveCustomer = async () => {
    if (toBeArchivedCustomer) {
      try {
        await archiveClient(toBeArchivedCustomer)
        toast.success("Customer archived successfully")
        setToBeArchivedCustomer(null)
      } catch (e) {
        const error = e as Error
        toast.error(error.message)
      }
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => ({
    className: globalStyles.messageTemplateButtonContainer,
    actionButtons: [
      {
        icon: <IconArchiveBox />,
        tooltip: "Archive",
        tooltipPosition: "top-start" as const,
        hidden: rowData.devices_count.value !== 0 || showArchived,
        handler: handleShowArchiveCustomerModal,
      },
    ],
  })

  const handleShowArchiveCustomerModal = (id: number) => {
    setToBeArchivedCustomer(id)
  }

  const onEditClick = async (id: number) => {
    if (userProfile?.type === 11) {
      onShowEditCustomerModal(id)
    } else {
      navigate(`/customers/${id}/details?openSidebar=true`)
    }
  }

  const handleSetSelectedCustomer = (id: number) => {
    setSelectedCustomer(prev => (prev.includes(id) ? [] : [id]))
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    sendRequest(urlQueryParams)
  }, [urlQueryParams, isFirstRender, showArchived])

  return (
    <>
      <WidgetsWrapper>
        <WidgetCustomersMobileUsage />
        <WidgetNewCustomersTrend />
      </WidgetsWrapper>
      <ContentWrapper
        title="Customers"
        renderHeaderAccessoryView={() => (
          <CustomersPageHeader
            showArchived={showArchived}
            urlQueryParams={urlQueryParams}
            tableHeaderCells={tableHeaderCells}
            selectedCustomer={selectedCustomer}
            data={data}
            onActivateSuccess={() => setSelectedCustomer([])}
            onShowArchivedChange={setShowArchived}
          />
        )}
      >
        <TableWrapper
          tableName="customers"
          onEditClick={onEditClick}
          onCheckboxChange={handleSetSelectedCustomer}
          headerCells={headerCells}
          columnsLoaded={!!columnsData}
          data={convertClientsToTableRows(showArchived ? archivedClients?.data : data?.data)}
          isLoading={isFetching || isFetchingArchived}
          getRowActions={getRowActions}
          currentItemIDs={selectedCustomer}
          isMobileDevice={isMobileDevice()}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {toBeArchivedCustomer && (
        <DeleteModal
          title="Archive customer"
          message="Are you sure you want to archive the customer?"
          icon={<IconArchiveBox />}
          onCancel={() => setToBeArchivedCustomer(null)}
          onConfirm={handleArchiveCustomer}
        />
      )}
      {renderEditCustomerModal()}
    </>
  )
}
